import React from 'react';

export interface TooltipDateRange {
    startDate: string;
    endDate?: string;
    // For balance-mismatch ranges: the closing balance on startDate and the
    // opening balance on endDate — the two amounts that fail to reconcile.
    startBalance?: number | null;
    endBalance?: number | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const headingStyle: React.CSSProperties = {
    fontFamily: 'var(--text-gilroy-semibold)',
    fontSize: '14px',
    color: '#1D1D1D',
    marginBottom: '8px',
};

const darkTextStyle: React.CSSProperties = {
    fontFamily: 'var(--text-gilroy-semibold)',
    fontSize: '13px',
    color: '#1D1D1D',
    whiteSpace: 'nowrap',
};

const greyTextStyle: React.CSSProperties = {
    fontFamily: 'var(--text-gilroy-medium)',
    fontSize: '12px',
    color: 'var(--label-color, #9D9D9D)',
    whiteSpace: 'nowrap',
};

// Parse a date-only string into a LOCAL-midnight Date so the displayed calendar
// day never shifts with the viewer's timezone (new Date('YYYY-MM-DD') parses at
// UTC midnight, which renders as the previous day west of UTC).
const parseDateOnly = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    if (year && month && day) return new Date(year, month - 1, day);
    return new Date(dateStr);
};

export const formatTooltipDate = (dateStr: string): string => {
    const date = parseDateOnly(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const inclusiveDays = (range: TooltipDateRange): number => {
    const start = new Date(range.startDate);
    const end = new Date(range.endDate || range.startDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 1;
    return Math.round((end.getTime() - start.getTime()) / DAY_MS) + 1;
};

// Merge ranges that are contiguous or overlapping — the backend splits missing-date
// ranges at month boundaries, so May 25–31 + Jun 1–5 is really one May 25 – Jun 5 range.
export const mergeAdjacentRanges = (ranges: TooltipDateRange[]): TooltipDateRange[] => {
    const sorted = [...ranges].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
    const merged: TooltipDateRange[] = [];
    for (const range of sorted) {
        const rangeEnd = range.endDate || range.startDate;
        const last = merged[merged.length - 1];
        if (last && new Date(range.startDate).getTime() <= new Date(last.endDate || last.startDate).getTime() + DAY_MS) {
            if (new Date(rangeEnd).getTime() > new Date(last.endDate || last.startDate).getTime()) {
                last.endDate = rangeEnd;
            }
        } else {
            merged.push({ ...range, endDate: rangeEnd });
        }
    }
    return merged;
};

// A range spanning multiple months is repeated under every month it touches — collapse to unique ranges.
export const dedupeRanges = (ranges: TooltipDateRange[]): TooltipDateRange[] => {
    const seen = new Set<string>();
    return ranges.filter((range) => {
        const key = `${range.startDate}_${range.endDate || range.startDate}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

// Calendar-day key (local YYYY-MM-DD) so ranges compare by day regardless of how the date string is spelled.
const toDayKey = (dateStr: string): string => {
    const date = parseDateOnly(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const rangeKey = (range: TooltipDateRange): string => `${toDayKey(range.startDate)}_${toDayKey(range.endDate || range.startDate)}`;

// Display-only: a missing-day range that is EXACTLY a balance-mismatch range (same start AND end day)
// is already conveyed by the mismatch entry, so it is dropped from the Missing Days list.
// Overlapping or adjacent-but-not-equal ranges are kept.
export const excludeRangesMatchingMismatch = (
    missingRanges: TooltipDateRange[],
    balanceMismatchRanges: TooltipDateRange[],
): TooltipDateRange[] => {
    if (balanceMismatchRanges.length === 0) return missingRanges;
    const mismatchKeys = new Set(balanceMismatchRanges.map(rangeKey));
    return missingRanges.filter((range) => !mismatchKeys.has(rangeKey(range)));
};

const formatBalance = (balance: number): string => `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(balance)}`;

interface MissingDaysTooltipContentProps {
    missingRanges: TooltipDateRange[];
    balanceMismatchRanges: TooltipDateRange[];
}

const MissingDaysTooltipContent = ({ missingRanges, balanceMismatchRanges }: MissingDaysTooltipContentProps) => {
    if (missingRanges.length === 0 && balanceMismatchRanges.length === 0) {
        return <div style={{ padding: '0.25rem' }}>All data present</div>;
    }

    const visibleMissingRanges = excludeRangesMatchingMismatch(missingRanges, balanceMismatchRanges);

    return (
        <div style={{ padding: '0.25rem', textAlign: 'left' }}>
            {visibleMissingRanges.length > 0 && (
                <>
                    <div style={headingStyle}>Missing Days</div>
                    {visibleMissingRanges.map((range, index) => {
                        const days = inclusiveDays(range);
                        const isSingleDay = !range.endDate || range.endDate === range.startDate;
                        return (
                            <div
                                key={`missing-${index}`}
                                style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '6px' }}
                            >
                                <span style={greyTextStyle}>•</span>
                                <span style={darkTextStyle}>
                                    {days} {days === 1 ? 'Day' : 'Days'}
                                </span>
                                <span style={greyTextStyle}>
                                    (
                                    {isSingleDay
                                        ? formatTooltipDate(range.startDate)
                                        : `${formatTooltipDate(range.startDate)} – ${formatTooltipDate(range.endDate || range.startDate)}`}
                                    )
                                </span>
                            </div>
                        );
                    })}
                </>
            )}

            {balanceMismatchRanges.length > 0 && (
                <>
                    <div
                        style={{
                            ...headingStyle,
                            color: 'var(--risk-indicator-red, #DF666A)',
                            marginTop: visibleMissingRanges.length > 0 ? '12px' : 0,
                        }}
                    >
                        Balance Mismatch
                    </div>
                    {balanceMismatchRanges.map((range, index) => (
                        <div key={`mismatch-${index}`} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <span style={greyTextStyle}>•</span>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'auto auto auto',
                                    columnGap: '12px',
                                    rowGap: '2px',
                                    alignItems: 'baseline',
                                }}
                            >
                                <span style={darkTextStyle}>{formatTooltipDate(range.startDate)}</span>
                                <span style={{ ...greyTextStyle, textAlign: 'center' }}>{'< >'}</span>
                                <span style={darkTextStyle}>{formatTooltipDate(range.endDate || range.startDate)}</span>
                                {(range.startBalance != null || range.endBalance != null) && (
                                    <>
                                        <span style={greyTextStyle}>
                                            {range.startBalance != null ? `(${formatBalance(range.startBalance)})` : ''}
                                        </span>
                                        <span />
                                        <span style={greyTextStyle}>
                                            {range.endBalance != null ? `(${formatBalance(range.endBalance)})` : ''}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
};

export default MissingDaysTooltipContent;
